import type { Block } from "building-types/blocks/blockTypes";
import type { ComponentTransform } from "building-types/components/componentTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { RWTable, Table } from "common/data/table";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { roomNew } from "../../elements/rooms/roomTools";
import { applyTransformToOutlineAndEdgeIds } from "../../utils/componentTransforms";
import { componentInstanceLevelId } from "./componentInstanceLevel";

export function getRoomComponentTransforms(
  roomId: string,
  idToParents: Table<readonly string[]>,
  componentToTransforms: Table<readonly ComponentTransform[]>,
): readonly ComponentTransform[] {
  const parentComponents = idToParents[roomId] || [];
  const transforms: ComponentTransform[] = [];
  for (const componentId of parentComponents) {
    transforms.push(...(componentToTransforms[componentId] || []));
  }
  return transforms;
}

/**
 * Apply a transform to a room and return the new room.
 */
export function applyTransformToRoom(
  room: Room,
  transform: ComponentTransform,
  levels: Table<Level>,
  blocks: RWTable<Block>,
  sourceBlock: Block,
): Room {
  const { instanceId, componentInstanceChain, verticalPlacement } = transform;

  const instanceRoomId = instanceIdForComponent(room.id, componentInstanceChain);
  const instanceBlockId = instanceIdForComponent(room.parentId, componentInstanceChain);

  const levelId = componentInstanceLevelId(room.level.id, levels, verticalPlacement, componentInstanceChain);

  // create instance block if needed
  if (!blocks[instanceBlockId]) {
    blocks[instanceBlockId] = {
      ...sourceBlock,
      id: instanceBlockId,
      levelId,
    };
  }

  const { transformedOutline, transformedEdgeIds } = applyTransformToOutlineAndEdgeIds(
    transform,
    room.outline,
    room.edgeIds,
  );

  return roomNew({
    ...room,
    id: instanceRoomId,
    level: levels[levelId],
    parentId: instanceBlockId,
    outline: transformedOutline,
    sourceRoomId: room.id,
    instanceId,
    name: room.name ?? instanceRoomId,
    edgeIds: transformedEdgeIds,
    componentTransform: transform,
  });
}
